import React, { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';

export type IconPathData = string | string[];
export type SizeProp = "xs" | "lg" | "sm" | "1x" | "2x" | "3x" | "4x" | "5x" | "6x" | "7x" | "8x" | "9x" | "10x";
export type FlipProp = "horizontal" | "vertical" | "both";
export type PullProp = "left" | "right";
export type RotateProp = 90 | 180 | 270;
export interface IconDefinition {
   icon: [
      number, // width
      number, // height
      string[], // ligatures
      string, // unicode
      IconPathData // svgPathData
   ];
}
export interface Transform {
   size?: number;
   x?: number;
   y?: number;
   rotate?: number;
   flipX?: boolean;
   flipY?: boolean;
}

/**
 * {@docCategory Icon}
 */
export interface IconProps {
   /**
    * The font awesome icon definition
    */
   icon: IconDefinition;

   /**
    * The icon size in fractional 'em' units.
    * @defaultvalue 1
    */
   size?: number | SizeProp;

   /**
    * Set the make the icon a fixed width
    * @defaultvalue false
    */
   fixedWidth?: boolean;

   /**
    * Invert the icon
    * @defaultvalue false
    */
   inverse?: boolean;

   /**
    * Spin the icon
    */
   spin?: boolean;

   /**
    * Pulse the icon
    */
   pulse?: boolean;

   /**
    * Show a border around the icon
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Show icon in a list
    * @defaultvalue false
    */
   listItem?: boolean;

   /**
    * Flip the icon
    */
   flip?: FlipProp;

   /**
    * Pull the icon
    */
   pull?: PullProp;

   /**
    * Rotate the icon
    */
   rotation?: RotateProp;

   /**
    * Transform the icon
    */
   transform?: string | Transform;

   /**
    * The tab index
    */
   tabIndex?: number;

   /**
    * The title
    */
   title?: string;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback when clicked.
    */
   onClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

export const Icon = (props: IconProps) => {
   const { icon, size, fixedWidth, inverse, spin, pulse, border, listItem, flip, pull, rotation, transform, tabIndex, title, style, className, onClick, ...otherProps } = props;

   const classNames = classnames('uf-icon', className, props.onClick ? 'uf-icon-clickable' : undefined);
   const newStyle = Object.assign({}, style);

   if (typeof size === 'number') {
      ((newStyle) as any).fontSize = size + 'em';
   }

   return (
      <FontAwesomeIcon
         icon={icon as any}
         size={typeof size === 'number' ? undefined : size}
         fixedWidth={fixedWidth}
         inverse={inverse}
         spin={spin}
         pulse={pulse}
         border={border}
         listItem={listItem}
         flip={flip}
         pull={pull}
         rotation={rotation}
         transform={transform}
         tabIndex={tabIndex}
         title={title}
         className={classNames}
         style={newStyle}
         onClick={onClick}
         {...otherProps}
      />
   );
};
